import { Link } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './Features.css';

const jumpLinks = [
  { label: 'Diet & Nutrition', href: '#diet' },
  { label: 'Exercise & Movement', href: '#exercise' },
  { label: 'Sleep & Mind', href: '#mind' },
  { label: 'Coaching & Insights', href: '#coaching' },
  { label: 'Integrations', href: '#integrations' },
];

const overviewGroups = [
  {
    label: 'Diet & Nutrition',
    items: [
      { title: 'Meal logging by text', desc: 'Describe any meal and mosaime logs calories, macros, and food quality automatically.' },
      { title: 'Meal logging by photo', desc: 'Send a photo of your plate and mosaime estimates and logs the meal.' },
      { title: 'Ingredient label scanning', desc: 'Photo of any food label: mosaime flags ultra-processed ingredients, misleading claims, and suggests swaps.' },
      { title: 'Recipes', desc: 'Get recipe suggestions aligned to your goals, on request or proactively. Save, rate, and share them.' },
      { title: 'Meal planning', desc: 'Build a week of meals around your preferences and dietary needs.' },
      { title: 'Grocery lists', desc: 'Auto-generated from your meal plan, editable by text, shareable with anyone.' },
      { title: 'Daily nutrition summary', desc: "A day's meals with macro breakdown, highlights, and suggestions for tomorrow." },
    ],
  },
  {
    label: 'Exercise & Movement',
    items: [
      { title: 'Workout logging', desc: 'Describe any workout and mosaime logs type, duration, and effort.' },
      { title: 'Custom single workouts', desc: 'Request a workout for any goal, time, or equipment situation.' },
      { title: 'Multi-week training plans', desc: 'Request a plan (e.g. "4-week 5K training") and get daily workout links by text, with a Sunday preview of the week ahead.' },
      { title: 'Plan editing by text', desc: 'Adjust your plan anytime: make it easier or harder, restructure it, take a rest week.' },
      { title: 'Natural movement tracking', desc: 'Post-meal walks, step targets, and movement throughout the day — tracked independently from structured workouts.' },
      { title: 'Streak feedback', desc: 'Your coach surfaces streaks and momentum to reinforce consistency.' },
    ],
  },
  {
    label: 'Sleep & Mental Health',
    items: [
      { title: 'Sleep tracking', desc: 'Log sleep by text or automatically via Fitbit.' },
      { title: 'Mood and stress check-ins', desc: "Share how you're feeling and mosaime tracks it alongside the physical." },
      { title: 'Stress tools', desc: 'Breathwork techniques, short walk nudges, journaling prompts — suggested in context.' },
      { title: 'Daily cognitive puzzles', desc: 'A morning set of brain games: memory grid, number sequences, lateral-thinking riddle, word association, and a word of the day.' },
    ],
  },
  {
    label: 'Coaching & Insights',
    items: [
      { title: 'Daily coaching brief', desc: "A morning link to your personalized focus, habit reminders, today's workout, and agenda." },
      { title: 'Weekly wellness summary', desc: 'Every Sunday: meals and workouts logged, goal progress, habit completion, trends vs. last week.' },
      { title: 'Wellness Mosaic', desc: 'One weekly image showing your decisions across diet, exercise, and mind as a mosaic of tiles.' },
      { title: 'Proactive outreach', desc: 'Recipe ideas, workout reminders, check-ins, and nudges — mosaime reaches out, not just responds.' },
      { title: 'Goal framework', desc: '15 focus areas across diet, mind, and exercise that your coaching always connects back to.' },
      { title: 'Habit tracking', desc: 'Recurring habits with schedules, reminders, and streak tracking.' },
      { title: 'Microhabit nudges', desc: 'Lighter in-the-moment tips, convertible to a simple SMS reminder.' },
      { title: 'Memory that improves', desc: 'mosaime gets more personal over time through layered short-term and long-term memory.' },
      { title: 'Local event suggestions', desc: 'Weekly suggestions for nearby classes, activities, and events based on your zip code and interests.' },
    ],
  },
  {
    label: 'Integrations',
    items: [
      { title: 'Fitbit', desc: 'Automatic step, active minutes, heart rate, and sleep data.' },
      { title: 'Shareable links', desc: 'Recipes, coaching briefs, workouts, grocery lists, and your mosaic are all public links you can save or share — no login required for recipients.' },
      { title: 'Daily log web page', desc: 'A full-day entry view linked via SMS for adding or correcting entries directly.' },
    ],
  },
];

type DeepDive = {
  id: string;
  title: string;
  intro: string;
  features: { label: string; paragraphs: string[] }[];
};

const deepDives: DeepDive[] = [
  {
    id: 'diet',
    title: 'Diet & Nutrition',
    intro:
      "Log what you eat, understand what's in it, and build better habits — all by text. No food databases to browse, no calorie counting by hand.",
    features: [
      {
        label: 'Meal logging by text',
        paragraphs: [
          'Describe what you ate in plain language — "scrambled eggs, toast with a little butter, black coffee" — and mosaime logs the meal type, estimates calories and macros (protein, carbs, fat, fiber, sugar, sodium), and rates the food against the NOVA framework: whole, minimally processed, processed, or ultra-processed. If something\'s ambiguous, mosaime asks one clarifying question (portion size, cooking method) and moves on.',
          'You can also backfill: "I had oatmeal this morning around 7" logs to the right time. Entries can be corrected or added on the daily log web page, linked via SMS each evening.',
        ],
      },
      {
        label: 'Meal logging by photo',
        paragraphs: [
          "Send a photo of your plate and mosaime identifies what's on it and logs the meal automatically. Useful for home-cooked meals and restaurant dishes where you don't have exact ingredients. mosaime notes where estimates are less certain and may ask a follow-up question.",
        ],
      },
      {
        label: 'Ingredient label scanning',
        paragraphs: [
          'Send a photo of any food product\'s ingredient label. mosaime returns a full breakdown: ingredient list, ultra-processed flag, detection of misleading label claims (like "natural" on a heavily processed item), nutrition highlights and warnings, and healthier swap suggestions. Delivered as a shareable scan page link.',
        ],
      },
      {
        label: 'Recipes',
        paragraphs: [
          'Ask for a recipe — "a quick high-protein dinner" — and mosaime suggests one aligned to your goals and preferences. You get a shareable recipe link with ingredients, instructions, nutrition focus, prep and cook time, difficulty, and wellness notes.',
          'Rate recipes after cooking (1–5 stars) and mosaime uses your feedback to sharpen future suggestions. Recipes integrate with meal plans and grocery lists.',
          'mosaime also suggests recipes proactively — based on your goals and what you\'ve been eating recently — without waiting for you to ask.',
        ],
      },
      {
        label: 'Meal planning and grocery lists',
        paragraphs: [
          'Tell mosaime you want to plan the week\'s meals — it asks a few questions (dietary restrictions, preferred cuisines, prep time, number of people) and generates a week of recipes plus an automatically built grocery list.',
          'The grocery list is editable by text ("add milk," "remove eggs") and available as a shareable page. mosaime sends cook reminders on planned cooking days.',
        ],
      },
      {
        label: 'Daily nutrition summary',
        paragraphs: [
          'Each evening, mosaime sends a link to your daily nutrition summary: all meals logged, a macro breakdown, highlights and warnings for the day, and one suggestion for tomorrow.',
        ],
      },
    ],
  },
  {
    id: 'exercise',
    title: 'Exercise & Movement',
    intro:
      'Log any workout, request a custom plan, and track movement the way it actually happens — in the gym, outdoors, and woven into daily life. No gym membership required.',
    features: [
      {
        label: 'Workout logging',
        paragraphs: [
          'Describe any workout in plain language — "30-minute run, felt good" or "strength session, squats and deadlifts, about 45 minutes" — and mosaime logs the activity type, duration, intensity, and effort. Any kind of movement counts: gym sessions, outdoor runs, yoga, walks, swimming, cycling, or anything else.',
        ],
      },
      {
        label: 'Custom workouts',
        paragraphs: [
          'Request a single workout for any situation: "a 20-minute HIIT session I can do at home with no equipment" or "an upper body gym session, about 45 minutes." You get a link with exercises, sets and reps or duration, and instructions.',
        ],
      },
      {
        label: 'Multi-week training plans',
        paragraphs: [
          'Request a structured plan — "4-week 5K training starting from scratch" or "a 4-week strength program" — and mosaime builds it and delivers a daily workout link by text. A Sunday preview arrives each week so you know what\'s coming.',
          'Adjust your plan anytime by text: make individual sessions easier or harder, restructure the week, take a scheduled rest week, or deactivate the plan. When you finish a plan, mosaime can extend it with a natural progression.',
        ],
      },
      {
        label: 'Natural movement',
        paragraphs: [
          "mosaime doesn't only track gym sessions. Daily movement — steps, post-meal walks, taking the stairs, avoiding long stretches of sitting — is independently important and tracked separately from structured workouts. mosaime encourages \"movement snacks\" throughout the day and uses Fitbit data (if connected) to give you step-count feedback alongside your other coaching.",
        ],
      },
      {
        label: 'Streaks and momentum',
        paragraphs: [
          'Your coach surfaces streaks and momentum as they build — not as pressure, but as information. Consistency over weeks matters more to mosaime than any single session.',
        ],
      },
    ],
  },
  {
    id: 'mind',
    title: 'Sleep & Mental Health',
    intro:
      'Tracked alongside the physical — because it belongs there. How you sleep and how you feel affects what you eat, how you move, and how you recover. mosaime treats mental wellness as a first-class input, not an afterthought.',
    features: [
      {
        label: 'Sleep tracking',
        paragraphs: [
          'Share how you slept — "slept about 6 hours, woke up a few times" — and mosaime logs sleep duration, quality, and likely patterns. Connect Fitbit and sleep data flows in automatically from your wearable, so you don\'t have to remember to report it.',
          'Your coach uses sleep data across the week to spot patterns and offer grounded advice — not just a nightly score.',
        ],
      },
      {
        label: 'Mood and stress check-ins',
        paragraphs: [
          'Share how you\'re feeling throughout the day and mosaime tracks mood and stress alongside everything else. Check-ins can be as short as "tired and stressed" or as detailed as you want — mosaime reads the context either way.',
        ],
      },
      {
        label: 'Stress tools',
        paragraphs: [
          'When stress comes up, mosaime has practical tools ready: box breathing techniques, short walk nudges, journaling prompts. These are offered in context — when you share something stressful — not pushed at you on a schedule.',
        ],
      },
      {
        label: 'Daily cognitive puzzles',
        paragraphs: [
          "Each morning around 9am, mosaime sends a link to your daily puzzle set: a memory grid, number sequences, a lateral-thinking riddle, a word association game, and a word of the day with progressive hints. Takes about 5–10 minutes and supports the cognitive engagement pillar of mosaime's wellness philosophy.",
        ],
      },
    ],
  },
  {
    id: 'coaching',
    title: 'Coaching & Insights',
    intro:
      "mosaime doesn't just log data and leave you to interpret it. It works toward specific goals with you, reaches out proactively, and shows you the whole picture in one view.",
    features: [
      {
        label: 'Daily coaching brief',
        paragraphs: [
          'Every morning, mosaime sends a link to your personalized coaching brief — a warm summary of where you stand, coaching notes for diet, exercise, and mind, a single focus for the day, your habit reminders, today\'s workout link if you have one, and anything coming up on your cooking or shopping agenda. The brief adapts to what\'s actually happening in your data, not a generic daily message.',
        ],
      },
      {
        label: 'Weekly wellness summary',
        paragraphs: [
          'Every Sunday, mosaime sends a link to your weekly summary: how many meals and workouts you logged, goal progress across your focus areas, habit completion rate, trends vs. the week before, and one habit-building suggestion for the week ahead.',
        ],
      },
      {
        label: 'Wellness Mosaic',
        paragraphs: [
          'Each week, mosaime sends your Wellness Mosaic as an image — a beeswarm where each point is a logged entry, colored by category (diet, exercise, mind) and shaded by quality. Deeper green reflects stronger decisions. Lighter points show where there\'s room to grow.',
          'A few dimmer points next to brighter ones isn\'t a problem to fix — that mix is what balance actually looks like. The goal isn\'t a perfect score. It\'s watching the whole picture consistently move in the right direction.',
        ],
      },
      {
        label: 'Proactive outreach',
        paragraphs: [
          "mosaime doesn't wait for you to start every conversation. Expect recipe suggestions aligned to your goals, workout reminders before planned sessions, proactive check-ins, and nudges when your coach notices a pattern worth flagging. The frequency adapts to you — if you want more or less, just say so.",
        ],
      },
      {
        label: 'Goal framework',
        paragraphs: [
          'mosaime organizes coaching around 15 fixed focus areas across three categories — Diet & Nutrition, Mind & Mental Wellness, and Exercise & Movement. Things like eating more whole foods at most meals, prioritizing consistent restful sleep, and finding a way to move that you\'ll actually enjoy. These are the throughlines every reply, nudge, and brief connects back to.',
        ],
      },
      {
        label: 'Habit tracking',
        paragraphs: [
          'For each focus area, mosaime suggests specific recurring habits from a curated list — meal prep, a short post-dinner walk, a breathing exercise. Each habit gets a schedule, a reminder, and tracking so you can see streaks and momentum build over time.',
        ],
      },
      {
        label: 'Microhabit nudges',
        paragraphs: [
          'Alongside tracked habits, mosaime surfaces lighter in-the-moment tips — small enough that logging them would be more hassle than help. You can turn any into a simple scheduled text reminder, but mosaime won\'t ask you to track or report back on them. The point is the nudge, not the record.',
        ],
      },
      {
        label: 'Memory that improves',
        paragraphs: [
          "mosaime gets more personal the longer you use it. Short-term memory captures situational facts — you're injured, you're trying intermittent fasting this week. Long-term profile holds durable traits: preferences, restrictions, schedule, fitness level. A weekly consolidation folds recent patterns into the long-term profile, so coaching keeps improving without you having to re-explain yourself.",
          'The current conversation always overrides older assumptions, so mosaime never awkwardly references something you\'ve already moved past.',
        ],
      },
      {
        label: 'Local event suggestions',
        paragraphs: [
          'Based on your zip code and stated interests, mosaime surfaces nearby classes, events, and activities each week — a practical nudge toward getting out and moving that\'s specific to where you actually live.',
        ],
      },
    ],
  },
];

export default function Features() {
  return (
    <div className="landing-page">
      <PublicHeader />

      {/* ---- HERO ---- */}
      <section className="feat-hero">
        <div className="landing-container">
          <h1>Everything mosaime can do.</h1>
          <p className="feat-hero-sub">
            One coach. Diet, exercise, sleep, and mental health — all tracked and coached together,
            over text.
          </p>
        </div>
      </section>

      {/* ---- STICKY JUMP NAV ---- */}
      <nav className="feat-jumpnav" aria-label="Jump to section">
        <div className="landing-container feat-jumpnav-inner">
          {jumpLinks.map((link) => (
            <a key={link.href} href={link.href} className="feat-jump-pill">{link.label}</a>
          ))}
        </div>
      </nav>

      {/* ---- QUICK-SCAN OVERVIEW GRID ---- */}
      <section className="section feat-overview">
        <div className="landing-container">
          <h2 className="section-title">Everything in one place.</h2>
          <p className="section-sub">
            No separate app for nutrition. No separate app for workouts. No separate app for sleep.
          </p>
          {overviewGroups.map((group) => (
            <div key={group.label} className="feat-overview-group">
              <div className="feat-overview-label">{group.label}</div>
              <div className="feat-overview-grid">
                {group.items.map((item) => (
                  <div key={item.title} className="feat-overview-card">
                    <h3>{item.title}</h3>
                    <p>{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---- DEEP DIVES ---- */}
      {deepDives.map((section, i) => (
        <section
          key={section.id}
          className={`section feat-deepdive ${i % 2 === 0 ? 'feat-deepdive-alt' : ''}`}
        >
          <div className="landing-container feat-deepdive-inner">
            <h2 id={section.id} className="feat-deepdive-title">{section.title}</h2>
            <p className="feat-deepdive-intro">{section.intro}</p>
            {section.features.map((feature) => (
              <div key={feature.label} className="feat-subfeature">
                <h3>{feature.label}</h3>
                {feature.paragraphs.map((p, j) => (
                  <p key={j}>{p}</p>
                ))}
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* ---- INTEGRATIONS & SHAREABLE LINKS ---- */}
      <section className="section feat-deepdive feat-deepdive-alt">
        <div className="landing-container feat-deepdive-inner">
          <h2 id="integrations" className="feat-deepdive-title">Integrations & shareable links</h2>

          <div className="feat-subfeature">
            <h3>Fitbit</h3>
            <p>
              Connect your Fitbit and mosaime automatically pulls in sleep patterns and daily
              activity — steps, active minutes, and heart rate. Your coach uses this real-world data
              to give more grounded advice, so recommendations reflect how you actually moved and
              rested, not just what you reported. Fitbit is optional — everything works without it.
            </p>
            <p>
              To connect: go to your account settings at <Link to="/settings">mosaime.com/settings</Link> and
              follow the Fitbit connection steps. mosaime reads your Fitbit data but never writes to it.
            </p>
          </div>

          <div className="feat-subfeature">
            <h3>Shareable links</h3>
            <p>
              Most rich views in mosaime are public, shareable links delivered over SMS — recipes,
              coaching briefs, workout plans, the Wellness Mosaic, grocery lists, food scans, and
              your daily log. No login required for recipients, so you can share a recipe or grocery
              list with anyone.
            </p>
            <p>Account management (settings, billing) is behind login.</p>
          </div>

          <div className="feat-subfeature">
            <h3>More integrations coming</h3>
            <p>
              We're working on expanding what mosaime connects to. If there's a specific device or
              platform you'd like supported, let us know at{' '}
              <a href="mailto:support@mosaime.com">support@mosaime.com</a>.
            </p>
          </div>
        </div>
      </section>

      {/* ---- FOOTER CTA ---- */}
      <section className="section cta-section">
        <div className="landing-container cta-inner">
          <h2>Ready to try it?</h2>
          <p className="cta-sub">Set up in 5 minutes. Your first 7 days are free.</p>
          <Link to="/signup" className="btn hero-cta">Get Started Free</Link>
          <p className="cta-secondary-links">
            <Link to="/how-it-works">How it works</Link>
            <span aria-hidden="true"> · </span>
            <Link to="/faq">FAQ</Link>
            <span aria-hidden="true"> · </span>
            <Link to="/philosophy">Our philosophy</Link>
          </p>
        </div>
      </section>

      {/* ---- FOOTER ---- */}
      <footer className="landing-footer">
        <div className="landing-container footer-inner">
          <div className="footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img footer-logo-icon-img" />
            <span>mosaime</span>
          </div>
          <div className="footer-links">
            <Link to="/features">Features</Link>
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/philosophy">Philosophy</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/login">Log in</Link>
            <Link to="/signup">Sign up</Link>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} mosaime. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
